import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { apiRequest } from "../../api/http";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchEmployeeDetail } from "../../store/slices/employeesSlice";
import { EmployeePayrollResponse, EmployeePayrollStatement, PayrollDetail } from "../../types/admin";
import { formatDate } from "../../utils/admin";
import { buildPayrollFileName, getPayrollFileUrl, getPayrollTitle, getPayrollTypeLabel, isPayrollFileOnly, isProbablyMockPayrollFileUrl } from "../../utils/payroll";

const EmployeePayrollDetailPage = () => {
  const { employeeId, payrollId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { detail, loadingDetail, detailError } = useAppSelector((state) => state.employees);

  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [allPayrolls, setAllPayrolls] = useState<EmployeePayrollStatement[]>([]);
  const [payroll, setPayroll] = useState<PayrollDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const payrollFileUrl = getPayrollFileUrl(payroll);
  const payrollIsFileOnly = isPayrollFileOnly(payroll);
  const canPreviewPayrollFile = Boolean(payrollFileUrl) && !isProbablyMockPayrollFileUrl(payrollFileUrl);

  useEffect(() => {
    if (employeeId && (!detail || detail.employee_id !== Number(employeeId))) {
      dispatch(fetchEmployeeDetail(Number(employeeId)));
    }
  }, [dispatch, employeeId, detail]);

  useEffect(() => {
    const fetchPayroll = async () => {
      if (!detail?.branch_id || !employeeId || !payrollId) return;

      setLoading(true);
      setError(null);
      try {
        const [listRes, detailRes] = await Promise.all([
          apiRequest<EmployeePayrollResponse>(`/admin/branches/${detail.branch_id}/employees/${employeeId}/payrolls`),
          apiRequest<PayrollDetail>(`/admin/branches/${detail.branch_id}/payrolls/${payrollId}`),
        ]);

        setAllPayrolls(listRes.items);
        setPayroll(detailRes);
      } catch (err: any) {
        setError(err.message || "급여명세서 정보를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    if (detail) {
      fetchPayroll();
    }
  }, [detail, employeeId, payrollId]);

  const handleDownloadPdf = async () => {
    if (!payroll) return;

    if (payrollIsFileOnly) {
      if (!canPreviewPayrollFile) {
        alert("다운로드할 급여명세서 파일이 없습니다.");
        return;
      }

      const link = document.createElement("a");
      link.href = payrollFileUrl;
      link.target = "_blank";
      link.rel = "noreferrer";
      link.download = payroll.uploaded_files?.[0]?.file_name || buildPayrollFileName(payroll.employee_name, payroll.year, payroll.month);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    const element = pdfContentRef.current;
    if (!element) {
      alert("PDF로 변환할 급여명세서 내용이 없습니다.");
      return;
    }

    setGeneratingPdf(true);
    try {
      const canvas = await html2canvas(element, { scale: 2, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(buildPayrollFileName(payroll.employee_name, payroll.year, payroll.month));
    } catch (err) {
      console.error("급여명세서 PDF 생성 실패", err);
      alert("PDF 생성에 실패했습니다.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <section className="admin-page" style={{ maxWidth: "100%", padding: "0 48px" }}>
      <header className="detail-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <button className="back-btn" type="button" onClick={() => navigate(-1)}>
            ←
          </button>

          {payroll && (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <select
                value={payroll.payroll_id}
                onChange={(event) => navigate(`/admin/employees/${employeeId}/payrolls/${event.target.value}`)}
                style={{
                  padding: "0 24px 0 0",
                  borderRadius: "0",
                  border: "none",
                  backgroundColor: "transparent",
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#1d1d1f",
                  cursor: "pointer",
                  margin: 0,
                  height: "auto",
                  backgroundPosition: "right center",
                  lineHeight: 1.2,
                }}
              >
                {allPayrolls.map((item) => (
                  <option key={item.payroll_id} value={item.payroll_id}>
                    {getPayrollTitle(item)}
                  </option>
                ))}
              </select>

              <div style={{ display: "flex", gap: "16px", color: "#666874", fontSize: "13px" }}>
                <span><strong>유형:</strong> {getPayrollTypeLabel(payroll)}</span>
                <span><strong>생성일:</strong> {formatDate(payroll.created_at)}</span>
                {!payrollIsFileOnly && <span><strong>실수령액:</strong> {payroll.net_pay.toLocaleString()}원</span>}
              </div>
            </div>
          )}
        </div>

        {payroll && (
          <div style={{ display: "flex", gap: "8px" }}>
            {payrollIsFileOnly && canPreviewPayrollFile && (
              <button
                type="button"
                onClick={() => window.open(payrollFileUrl, "_blank", "noopener,noreferrer")}
                style={{
                  backgroundColor: "white",
                  color: "#1d1d1f",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "14px",
                  cursor: "pointer",
                  border: "1px solid #e7e8ef",
                }}
              >
                파일 보기
              </button>
            )}
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={generatingPdf || (payrollIsFileOnly && !canPreviewPayrollFile)}
              style={{
                backgroundColor: "#1d1d1f",
                color: "white",
                padding: "10px 16px",
                borderRadius: "8px",
                fontWeight: "500",
                fontSize: "14px",
                cursor: payrollIsFileOnly && !canPreviewPayrollFile ? "not-allowed" : generatingPdf ? "wait" : "pointer",
                border: "none",
                opacity: payrollIsFileOnly && !canPreviewPayrollFile ? 0.5 : 1,
              }}
            >
              {generatingPdf ? "PDF 생성 중..." : payrollIsFileOnly ? "파일 다운로드" : "PDF 다운로드"}
            </button>
          </div>
        )}
      </header>

      {loadingDetail && <p className="loading-text">직원 정보를 불러오는 중입니다...</p>}
      {detailError && <p className="error-text">{detailError}</p>}
      {loading && <p className="loading-text">데이터를 불러오는 중입니다...</p>}
      {error && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100vh - 200px)", color: "#ff4d4f" }}>
          <p>{error}</p>
        </div>
      )}

      {!loadingDetail && !detailError && !loading && !error && payroll && payrollIsFileOnly && (
        <div style={{
          border: "1px solid #e7e8ef",
          borderRadius: "12px",
          overflow: "hidden",
          height: "calc(100vh - 200px)",
          backgroundColor: "white"
        }}>
          {canPreviewPayrollFile ? (
            <iframe
              src={`${payrollFileUrl}#toolbar=0`}
              width="100%"
              height="100%"
              style={{ border: "none", display: "block" }}
              title="Payroll PDF Viewer"
            />
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#666874", padding: "32px", textAlign: "center" }}>
              <div>
                <h3 style={{ margin: "0 0 12px", color: "#1d1d1f", fontSize: "18px" }}>급여명세서 파일을 불러오지 못했습니다.</h3>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  등록된 파일을 찾을 수 없거나 아직 준비되지 않았습니다.<br />
                  잠시 후 다시 시도하거나 관리자에게 문의해 주세요.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {!loadingDetail && !detailError && !loading && !error && payroll && !payrollIsFileOnly && (
        <div ref={pdfContentRef} style={{ backgroundColor: "white", padding: "32px", borderRadius: "20px", marginTop: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "40px" }}>
            <div>
              <h3 style={{ fontSize: "24px", fontWeight: "700", color: "#1d1d1f", margin: "0 0 8px" }}>
                {getPayrollTitle(payroll)}
              </h3>
              <div style={{ display: "flex", gap: "24px", color: "#1d1d1f", fontSize: "16px" }}>
                <span><strong>근무자</strong> {payroll.employee_name}</span>
                <span><strong>입사일</strong> {payroll.hire_date || "-"}</span>
                <span><strong>퇴사일</strong> {payroll.resignation_date || "-"}</span>
              </div>
            </div>
            <span style={{ color: "#666874", fontSize: "14px" }}>발급일 {formatDate(payroll.created_at)}</span>
          </div>

          <div style={{ display: "flex", gap: "40px" }}>
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "20px", paddingBottom: "16px", borderBottom: "1px solid #e7e8ef" }}>
                급여명세
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <DetailRow label="주민번호" value={payroll.resident_id_masked || "-"} />
                <DetailRow label="총 근무 시간" value={`${payroll.total_work_minutes.toLocaleString()} 분`} />
                <DetailRow label="시급" value={`${payroll.hourly_wage.toLocaleString()} 원`} />
                <DetailRow label="기본급" value={`${payroll.base_pay.toLocaleString()} 원`} />
                <DetailRow label="주휴수당" value={`${payroll.weekly_allowance.toLocaleString()} 원`} />
                <DetailRow label="기타정산금" value={`${(payroll.other_settlement_amount ?? 0).toLocaleString()} 원`} />
                <DetailRow label="연장수당" value={`${payroll.overtime_pay.toLocaleString()} 원`} />
                <DetailRow label="지급 총액" value={`${payroll.gross_salary.toLocaleString()} 원`} bold />
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "20px", paddingBottom: "16px", borderBottom: "1px solid #e7e8ef" }}>
                공제항목
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <DetailRow label="국민연금" value={`${payroll.national_pension.toLocaleString()} 원`} />
                <DetailRow label="건강보험" value={`${payroll.health_insurance.toLocaleString()} 원`} />
                <DetailRow label="고용보험" value={`${payroll.employment_insurance.toLocaleString()} 원`} />
                <DetailRow label="장기요양보험료" value={`${payroll.long_term_care_insurance.toLocaleString()} 원`} />
                <DetailRow label="소득세" value={`${payroll.income_tax.toLocaleString()} 원`} />
                <DetailRow label="지방소득세" value={`${payroll.local_income_tax.toLocaleString()} 원`} />
                <div style={{ marginTop: "24px", paddingTop: "24px", borderTop: "1px solid #e7e8ef" }}>
                  <DetailRow label="공제 총액" value={`${payroll.total_deduction.toLocaleString()} 원`} bold />
                  <div style={{ marginTop: "20px" }}>
                    <DetailRow label="실 수령액" value={`${payroll.net_pay.toLocaleString()} 원`} bold color="#5696ff" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const DetailRow = ({ label, value, bold = false, color = "#1d1d1f" }: { label: string; value: string; bold?: boolean; color?: string }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
    <span style={{ fontSize: "14px", fontWeight: "500", color: "#1d1d1f" }}>{label}</span>
    <div style={{ backgroundColor: "#fbfbfb", border: "1px solid #e7e8ef", borderRadius: "12px", padding: "16px" }}>
      <span style={{ fontSize: "14px", fontWeight: bold ? "600" : "400", color }}>{value}</span>
    </div>
  </div>
);

export default EmployeePayrollDetailPage;
